import { useEffect, useState, useCallback } from 'react';
import { Loader2, Save, Shield, ShieldOff } from 'lucide-react';
import { getOrgInfo } from '@/services/org/get-org-info';
import { Org, OrgSSO } from '@/types';
import { useNotification } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TagInput } from '@/components/ui/tag-input';
import { LoadingSpinner } from '@/components/loading-spinner';
import { apiRequest } from '@/utils/api-request';

interface OrgSsoTabProps {
  orgId: string;
}

const EMPTY_SSO: OrgSSO = {
  provider: '',
  auth0_org_id: '',
  auth0_connection_id: '',
  allowed_domains: [],
};

export const OrgSsoTab = ({ orgId }: OrgSsoTabProps) => {
  const [org, setOrg] = useState<Org | null>(null);
  const [sso, setSso] = useState<OrgSSO>(EMPTY_SSO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const notification = useNotification();

  const fetchOrgData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getOrgInfo(orgId);

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      if (response?.data) {
        const orgData = response.data as unknown as Org;
        setOrg(orgData);
        setSso(orgData.sso || EMPTY_SSO);
      }
    } catch (error) {
      console.error('Error fetching org info:', error);
      notification.error('Failed to load organization SSO data');
    } finally {
      setLoading(false);
    }
  }, [orgId, notification]);

  useEffect(() => {
    if (orgId) {
      fetchOrgData();
    }
  }, [orgId, fetchOrgData]);

  const handleSave = async () => {
    if (!org) return;

    setSaving(true);
    try {
      // Build the SSO object, omitting empty strings
      const ssoPayload: OrgSSO = {};
      if (sso.provider) ssoPayload.provider = sso.provider;
      if (sso.auth0_org_id) ssoPayload.auth0_org_id = sso.auth0_org_id;
      if (sso.auth0_connection_id)
        ssoPayload.auth0_connection_id = sso.auth0_connection_id;
      if (sso.allowed_domains && sso.allowed_domains.length > 0)
        ssoPayload.allowed_domains = sso.allowed_domains;

      const response = await apiRequest({
        url: `/organizations/${org.orgid}`,
        method: 'PUT',
        target: 'platform',
        body: {
          name: org.name,
          branding: org.branding,
          sso: Object.keys(ssoPayload).length > 0 ? ssoPayload : null,
        },
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('SSO configuration saved');
    } catch (error) {
      console.error('Error saving SSO config:', error);
      notification.error('Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!org) return;

    setSaving(true);
    try {
      const response = await apiRequest({
        url: `/organizations/${org.orgid}`,
        method: 'PUT',
        target: 'platform',
        body: {
          name: org.name,
          branding: org.branding,
          sso: null,
        },
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      setSso(EMPTY_SSO);
      notification.success('SSO configuration removed');
    } catch (error) {
      console.error('Error clearing SSO config:', error);
      notification.error('Failed to remove SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const hasSsoConfig =
    sso.auth0_org_id ||
    sso.auth0_connection_id ||
    sso.provider ||
    (sso.allowed_domains && sso.allowed_domains.length > 0);

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        <span className='ml-2 text-muted-foreground'>
          Loading SSO settings...
        </span>
      </div>
    );
  }

  if (!org) {
    return (
      <div className='flex items-center justify-center py-12'>
        <p className='text-muted-foreground'>
          Failed to load organization data
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <Card>
        <CardContent className='flex flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              {hasSsoConfig ? (
                <Shield className='h-4 w-4 text-green-600' />
              ) : (
                <ShieldOff className='h-4 w-4 text-muted-foreground' />
              )}
              <span className='font-medium'>
                SSO {hasSsoConfig ? 'Enabled' : 'Not Configured'}
              </span>
            </div>
          </div>

          <div className='grid gap-4'>
            <div className='grid gap-2'>
              <Label htmlFor='sso-provider'>Provider</Label>
              <Input
                id='sso-provider'
                placeholder='e.g., microsoft'
                value={sso.provider || ''}
                onChange={(e) =>
                  setSso((prev) => ({ ...prev, provider: e.target.value }))
                }
              />
              <p className='text-xs text-muted-foreground'>
                Identity provider type (e.g., &quot;microsoft&quot; for Azure
                AD)
              </p>
            </div>

            <div className='grid gap-2'>
              <Label htmlFor='sso-auth0-org'>Auth0 Organization ID</Label>
              <Input
                id='sso-auth0-org'
                placeholder='e.g., org_wDw6ehkmFCKuJT6R'
                value={sso.auth0_org_id || ''}
                onChange={(e) =>
                  setSso((prev) => ({ ...prev, auth0_org_id: e.target.value }))
                }
              />
              <p className='text-xs text-muted-foreground'>
                Auth0 Organization ID — found in Auth0 Dashboard under
                Organizations
              </p>
            </div>

            <div className='grid gap-2'>
              <Label htmlFor='sso-connection'>Auth0 Connection ID</Label>
              <Input
                id='sso-connection'
                placeholder='e.g., con_2N47PmkK2USJhd2c'
                value={sso.auth0_connection_id || ''}
                onChange={(e) =>
                  setSso((prev) => ({
                    ...prev,
                    auth0_connection_id: e.target.value,
                  }))
                }
              />
              <p className='text-xs text-muted-foreground'>
                Enterprise Connection ID or name from Auth0
              </p>
            </div>

            <div className='grid gap-2'>
              <Label>Allowed Email Domains</Label>
              <TagInput
                value={sso.allowed_domains || []}
                onChange={(domains) =>
                  setSso((prev) => ({ ...prev, allowed_domains: domains }))
                }
                placeholder='Type a domain and press Enter (e.g., ey.com)'
              />
              <p className='text-xs text-muted-foreground'>
                Email domains that can use SSO login for this organization
              </p>
            </div>
          </div>

          <div className='flex gap-2 pt-2'>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <LoadingSpinner /> Saving...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' /> Save SSO Configuration
                </>
              )}
            </Button>
            {hasSsoConfig && (
              <Button variant='outline' onClick={handleClear} disabled={saving}>
                Remove SSO
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
